import { useCallback, useState } from "react";
import BuyFulfillmentModal from "./BuyFulFillmentModal";
import AddOrEditFulfillmentModal from "./AddOrEditFulfillmentModal";
import { Button } from "../common/Button";
import { gql, useMutation } from "@apollo/client";
import { FullfillmentType, OrderPage_GetExistingOrderQuery, OrderPage_SendPickupReminderSmsMutation, OrderPage_SendPickupReminderSmsMutationVariables, SyncOrderFullfillmentsMutation, SyncOrderFullfillmentsMutationVariables } from "../../../codegen/graphql";
import OrderPageFulfillmentRow from "./OrderPageFulfillmentRow";
import { useRequestManager } from "../common/RequestManager";
import syncLeboncoinOrderFromFrontend from "../../lib/syncLeboncoinOrderFromFrontend";


export default function OrderPageFulfillments({
  orderData,
}: {
  orderData: NonNullable<OrderPage_GetExistingOrderQuery['order']>;
}) {
  const { trackRequest } = useRequestManager();

  // Mutations used
  const [syncOrderFullfillmentsMutation] = useMutation<
    SyncOrderFullfillmentsMutation,
    SyncOrderFullfillmentsMutationVariables
  >(
    SYNC_ORDER_FULLFILLMENTS_MUTATION,
  );
  const [deleteOrderFulfillmentMutation] = useMutation(
    DELETE_ORDER_FULFILLMENT_MUTATION,
  );
  const [sendPickupReminderSMSMutation] = useMutation<
    OrderPage_SendPickupReminderSmsMutation,
    OrderPage_SendPickupReminderSmsMutationVariables
  >(SEND_PICKUP_REMINDER_SMS_MUTATION);

  const syncFulfillments = useCallback(async () => {
    let syncPromise: Promise<any>;
    if ((orderData as any).shop.shopType === 'leboncoin') {
      // Syncing from frontend right now through browser extension, special case
      syncPromise = syncLeboncoinOrderFromFrontend(
        orderData.shopId,
        orderData.id!,
        orderData.orderId!,
      );
    } else {
      // Normal case
      syncPromise = syncOrderFullfillmentsMutation({
        variables: {
          orderId: orderData.id,
          externalOrderData: null,
        }
      })
    }

    // Launch request
    trackRequest(
      syncPromise,
      {
        title: 'Syncing fulfillments',
        isBlocking: false,
      }
    )
  }, [orderData.id, orderData.orderId]);

  const deleteFulfillment = useCallback(async (fulfillment: NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderFulfillments'][0]) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this fulfillment?\n\nPlatform: ${fulfillment.fullfilledPlatform || 'N/A'}\nOrder ID: ${fulfillment.fullfilledOrderId || 'N/A'}\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    trackRequest(
      deleteOrderFulfillmentMutation({
        variables: { id: fulfillment.id }
      }),
      {
        title: 'Deleting fulfillment',
        isBlocking: false,
      }
    );
  }, [deleteOrderFulfillmentMutation, trackRequest]);

  // State for fulfillments
  const [showBuyFulfillmentModal, setShowBuyFulfillmentModal] = useState(false);
  const [showAddOrEditModal, setShowAddOrEditModal] = useState(false);
  const [editingFulfillment, setEditingFulfillment] = useState<NonNullable<OrderPage_GetExistingOrderQuery['order']>['orderFulfillments'][0] | null>(null);
  

  const outwardFulfillments = orderData.orderFulfillments.filter((fulfillment) => (
    fulfillment.type === FullfillmentType.Outward
  ));
  const returnFulfillments = orderData.orderFulfillments.filter((fulfillment) => (
    fulfillment.type === FullfillmentType.Return
  ));

  return (
    <>
      <div className="flex flex-row justify-start gap-2 mb-2">
        <Button 
          preset="white" 
          size="md" 
          onClick={() => {
            setEditingFulfillment(null);
            setShowBuyFulfillmentModal(true);
          }}
        >
          Buy Fulfillment
        </Button>
        <Button 
          preset="white" 
          size="md" 
          onClick={() => {
            setEditingFulfillment(null);
            setShowAddOrEditModal(true);
          }}
        >
          Add Fulfillment
        </Button>
        <Button
          preset="white"
          onClick={syncFulfillments}>
          Sync Fulfillments
        </Button>
        <Button
          preset="white"
          onClick={() => {
            if (confirm('Are you sure you want to send the pickup reminder SMS?')) {
              trackRequest(
                sendPickupReminderSMSMutation({ variables: { orderId: orderData.id } }),
                {
                  title: 'Sending pickup reminder SMS',
                  isBlocking: true,
                }
              );
            }
          }}
        >
          Send Pickup Reminder SMS
        </Button>
      </div>
        
      {/* Fulfillments List */}
      <h2 className="text-lg font-medium text-gray-900 mb-2">Outward</h2>
      {outwardFulfillments.map((orderFulfillment) => (
        <OrderPageFulfillmentRow
          key={orderFulfillment.id}
          orderFulfillment={orderFulfillment}
          translationLang={orderData.customerAddressCountryCode}
          onEdit={(fulfillment) => {
            setEditingFulfillment(fulfillment);
            setShowAddOrEditModal(true);
          }}
          onDelete={deleteFulfillment}
        />
      ))}

      <h2 className="text-lg font-medium text-gray-900 mb-2">Return</h2>
      {returnFulfillments.map((orderFulfillment) => (
        <OrderPageFulfillmentRow
          key={orderFulfillment.id}
          orderFulfillment={orderFulfillment}
          translationLang={orderData.customerAddressCountryCode}
          onEdit={(fulfillment) => {
            setEditingFulfillment(fulfillment);
            setShowAddOrEditModal(true);
          }}
          onDelete={deleteFulfillment}
        />
      ))}
      
      {orderData.orderFulfillments.length === 0 && (
        <div className="text-center py-4 text-gray-500">No fulfillments found</div>
      )}
      
      {/* Fulfillment Modal */}
      {showBuyFulfillmentModal && (
        <BuyFulfillmentModal 
          orderId={orderData.id} 
          onClose={() => {
            setShowBuyFulfillmentModal(false);
            setEditingFulfillment(null);
          }}
          onSuccess={() => {
            setShowBuyFulfillmentModal(false);
            setEditingFulfillment(null);
          }}
        />
      )}

      {/* Register Fulfillment Modal */}
      {showAddOrEditModal && (
        <AddOrEditFulfillmentModal 
          orderId={orderData.id}
          fulfillmentId={editingFulfillment?.id}
          onClose={() => {
            setShowAddOrEditModal(false);
            setEditingFulfillment(null);
          }}
          onSuccess={() => {
            setShowAddOrEditModal(false);
            setEditingFulfillment(null);
          }}
        />
      )}
    </>
  );
}


export const SEND_PICKUP_REMINDER_SMS_MUTATION = gql`
  mutation OrderPage_SendPickupReminderSMS($orderId: ID!) {
    sendPickupReminderSMS(orderId: $orderId)
  }
`;

export const SYNC_ORDER_FULLFILLMENTS_MUTATION = gql`
  mutation SyncOrderFullfillments($orderId: ID!, $externalOrderData: JSON) {
    syncOrderFullfillments(orderId: $orderId, externalOrderData: $externalOrderData) {
      id
      orderStatus
      shopId
      shop {
        shopType
      }
      orderFulfillments {
        id
        fullfilledStatus
        fullfilledPlatform
        shippingCarrier
        trackingNumber
      }
    }
  }
`;

export const DELETE_ORDER_FULFILLMENT_MUTATION = gql`
  mutation DeleteOrderFulfillment($id: ID!) {
    deleteOrderFulfillment(id: $id)
  }
`;

